import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './VehiculeModal.css';

const STATUT_COLORS = {
  // Parking
  'disponible': '#10b981', 'en_attente': '#f59e0b', 'vendu': '#6b7280',
  // Réception VIP
  'recu': '#3b82f6', 'en_diagnostic': '#f59e0b', 'pieces_commande': '#a855f7',
  'attente_garantie': '#06b6d4', 'attente_accord': '#ec4899', 'pret': '#10b981', 'livre': '#6b7280',
  // OR / demandes
  'en-cours': '#3b82f6', 'archive': '#6b7280', 'clos': '#6b7280',
};

function dotColor(statut) {
  if (!statut) return '#cbd5e1';
  const s = statut.toLowerCase();
  return STATUT_COLORS[s] || '#3b82f6';
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

/* ─── Ligne générique ─────────────────────────────── */
function VmRow({ color, main, sub, badge, badgeBg, badgeColor, onClick }) {
  return (
    <button className="vm-row" onClick={onClick} type="button">
      <span className="vm-row-dot" style={{ background: color || '#cbd5e1' }} />
      <span className="vm-row-main">{main}</span>
      {badge && (
        <span
          className="vm-row-badge"
          style={{ background: badgeBg || '#e2e8f0', color: badgeColor || '#475569' }}
        >
          {badge}
        </span>
      )}
      {sub && <span className="vm-row-sub">{sub}</span>}
    </button>
  );
}

/* ─── Section ─────────────────────────────────────── */
function VmSection({ icon, title, items, emptyLabel, children }) {
  const count = items?.length ?? 0;
  return (
    <div className="vm-section">
      <div className="vm-section-header">
        <span className="vm-section-title">{icon} {title}</span>
        <span className={`vm-count${count > 0 ? ' has-items' : ''}`}>{count}</span>
      </div>
      <div className="vm-rows">
        {count === 0
          ? <span className="vm-empty">{emptyLabel}</span>
          : children
        }
      </div>
    </div>
  );
}

/* ─── Modal principale ────────────────────────────── */
export default function VehiculeModal({ immat, onClose, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const norm = immat.replace(/[-\s]/g, '').toUpperCase();
    api.get(`/vehicule/${encodeURIComponent(norm)}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [immat]);

  const nav = (view) => { onNavigate(view); onClose(); };

  return (
    <div className="vm-overlay" onClick={onClose}>
      <div className="vm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="vm-header">
          <div className="vm-header-left">
            <span className="vm-immat">{immat.replace(/[-\s]/g, '').toUpperCase()}</span>
            {data?.marque && (
              <span className="vm-vehicule-info">
                {data.marque}{data.modele ? ` ${data.modele}` : ''}
              </span>
            )}
          </div>
          <button className="vm-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        {/* Body */}
        <div className="vm-body">
          {loading ? (
            <div className="vm-loading">Chargement…</div>
          ) : !data ? (
            <div className="vm-loading">Erreur de chargement</div>
          ) : (
            <>
              {/* Demandes */}
              <VmSection icon="💬" title="Demandes clients" items={data.demandes} emptyLabel="Aucune demande">
                {data.demandes.map(d => (
                  <VmRow
                    key={d.id}
                    color={d.statut === 'archive' ? '#6b7280' : '#3b82f6'}
                    main={`${d.sujet || '—'}${d.nom ? ` — ${d.nom}${d.prenom ? ' ' + d.prenom : ''}` : ''}`}
                    badge={d.statut}
                    badgeBg={d.statut === 'archive' ? '#f1f5f9' : '#dbeafe'}
                    badgeColor={d.statut === 'archive' ? '#6b7280' : '#1d4ed8'}
                    sub={fmt(d.date_demande)}
                    onClick={() => nav('leads-services')}
                  />
                ))}
              </VmSection>

              {/* OR en cours */}
              <VmSection icon="🔩" title="OR en cours" items={data.ors} emptyLabel="Aucun OR">
                {data.ors.map(o => (
                  <VmRow
                    key={o.id}
                    color={dotColor(o.statut)}
                    main={`OR ${o.num_or || o.id}${o.nom_client ? ` — ${o.nom_client}` : ''}`}
                    badge={o.statut || o.action}
                    badgeBg="#fef3c7"
                    badgeColor="#92400e"
                    sub={fmt(o.date_creation)}
                    onClick={() => nav('or-en-cours')}
                  />
                ))}
              </VmSection>

              {/* Parking */}
              <VmSection icon="🅿️" title="Parking" items={data.parking} emptyLabel="Absent du parking">
                {data.parking.map(p => (
                  <VmRow
                    key={p.id}
                    color={dotColor(p.statut)}
                    main={`Allée ${p.allee || '—'}${p.concession ? ` · ${p.concession}` : ''}`}
                    badge={p.statut}
                    badgeBg="#f0fdf4"
                    badgeColor="#166534"
                    sub={fmt(p.updated_at)}
                    onClick={() => nav('parking')}
                  />
                ))}
              </VmSection>

              {/* Litiges */}
              <VmSection icon="⚖️" title="Litiges" items={data.litiges} emptyLabel="Aucun litige">
                {data.litiges.map(l => (
                  <VmRow
                    key={l.id}
                    color={l.statut_couleur || '#ef4444'}
                    main={`Litige #${l.id}${l.nom ? ` — ${l.nom}${l.prenom ? ' ' + l.prenom : ''}` : ''}`}
                    badge={l.statut}
                    badgeBg={l.statut_couleur ? l.statut_couleur + '22' : '#fee2e2'}
                    badgeColor={l.statut_couleur || '#dc2626'}
                    sub={fmt(l.date_ouverture)}
                    onClick={() => nav('litiges')}
                  />
                ))}
              </VmSection>

              {/* Réception VIP */}
              <VmSection icon="🏁" title="Réception VIP" items={data.vip} emptyLabel="Aucune réception">
                {data.vip.map(v => (
                  <VmRow
                    key={v.id}
                    color={dotColor(v.statut)}
                    main={`Dossier #${v.id}${v.client_username ? ` — ${v.client_username}` : ''}`}
                    badge={v.statut}
                    badgeBg="#f0f9ff"
                    badgeColor="#0369a1"
                    sub={v.date_restitution ? `Restitution ${fmt(v.date_restitution)}` : fmt(v.created_at)}
                    onClick={() => nav('reception-vip')}
                  />
                ))}
              </VmSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
